import React, { useContext, useEffect, useRef, useState } from "react";
import Router from "next/router";
import { useSession } from "next-auth/react";
import { ThemeContext } from "grommet";
import { normalizeColor } from "grommet/utils";
import styled from "styled-components";

/*
    A thin progress bar across the top of the window during a route change.

    Navigating in this app is a client-side transition: the browser gives no loading
    feedback of its own, so between the tap and the next screen there is nothing to say
    anything is happening. On a slow page that reads as a dead click.

    Two details keep it from becoming noise:

    - It waits before appearing. Most navigations resolve in well under the delay below,
      and a bar that flickers on every one of those is worse than no bar. Only a
      transition slow enough to be noticed gets an indicator.
    - It creeps towards a ceiling rather than to the end. Real progress is unknowable
      here, so it slows as it advances and only completes when the route actually does,
      instead of sitting at 100% while the page is still coming.
*/

// Below this, a navigation is quick enough that an indicator would only flash.
const SHOW_AFTER_MS = 180;
// Where the creep stops. The gap is what completing has left to fill.
const CREEP_CEILING = 90;
const CREEP_TICK_MS = 200;
// Long enough for the fill to 100% to be seen before it goes.
const FADE_OUT_MS = 220;

/*
    Width is set inline rather than interpolated here. Every distinct value in a styled
    template mints its own class and injects a rule, and this one changes five times a
    second for as long as a navigation runs. Colour and opacity stay: they have one value
    each per company and two overall.
*/
const Bar = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    height: 3px;
    /* Above the sticky page headers (20), below nothing else that matters: a 3px strip
       over a modal is the behaviour we want, not a bug. */
    z-index: 60;
    background: ${props => props.$color};
    opacity: ${props => (props.$visible ? 1 : 0)};
    transition: width 200ms ease-out, opacity ${FADE_OUT_MS}ms ease-out;
    pointer-events: none;
    will-change: width, opacity;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

const RouteProgressBar = () => {
    const { data: session } = useSession();
    const theme = useContext(ThemeContext);
    const [progress, setProgress] = useState(0);
    const [visible, setVisible] = useState(false);

    // Held in refs so the route handlers below can clear them without re-subscribing.
    const showTimer = useRef(null);
    const creepTimer = useRef(null);
    const hideTimer = useRef(null);
    /*
        Mirrors `visible` for the handlers. Reading the state directly would mean listing it
        as a dependency, and re-subscribing tears down the creep timer that had just been
        started - the bar would appear and then freeze.
    */
    const visibleRef = useRef(false);

    useEffect(() => {
        const clearTimers = () => {
            [showTimer, creepTimer, hideTimer].forEach((t) => {
                if (t.current) {
                    clearTimeout(t.current);
                    clearInterval(t.current);
                    t.current = null;
                }
            });
        };

        const start = () => {
            clearTimers();
            showTimer.current = setTimeout(() => {
                visibleRef.current = true;
                setVisible(true);
                setProgress(12);
                /*
                    Each tick closes a fraction of what is left, so it moves quickly at
                    first and slows as it approaches the ceiling. A linear creep either
                    arrives too early and sits there, or crawls from the start.
                */
                creepTimer.current = setInterval(() => {
                    setProgress((prev) => {
                        const next = prev + (CREEP_CEILING - prev) * 0.18;
                        /*
                            Snapped to the ceiling once the step stops being visible.
                            Approaching it asymptotically means every tick is a slightly
                            different number for as long as the navigation hangs, and each
                            one is a render. Landing exactly on it lets React bail out.
                        */
                        return next > CREEP_CEILING - 0.5 ? CREEP_CEILING : next;
                    });
                }, CREEP_TICK_MS);
            }, SHOW_AFTER_MS);
        };

        const done = () => {
            const wasVisible = visibleRef.current;
            clearTimers();
            if (!wasVisible) {
                // Resolved before the bar ever appeared; leave the screen untouched.
                setProgress(0);
                return;
            }
            setProgress(100);
            hideTimer.current = setTimeout(() => {
                visibleRef.current = false;
                setVisible(false);
                setProgress(0);
            }, FADE_OUT_MS);
        };

        Router.events.on("routeChangeStart", start);
        Router.events.on("routeChangeComplete", done);
        Router.events.on("routeChangeError", done);

        return () => {
            Router.events.off("routeChangeStart", start);
            Router.events.off("routeChangeComplete", done);
            Router.events.off("routeChangeError", done);
            clearTimers();
        };
    }, []);

    // The merchant's own colour, the app's brand when they have not set one.
    const color = session?.company?.brand_color || normalizeColor("brand", theme);

    return (
        <Bar
            aria-hidden="true"
            style={{width: `${progress}%`}}
            $color={color}
            $visible={visible} />
    );
};

export default RouteProgressBar;
